/**
 * The route table. Real URLs, real browser history, one screen per address.
 *
 * Two things are deliberate here:
 *
 *  · **Every screen is `React.lazy`.** Reports pulls in Recharts, exports pull in
 *    SheetJS; an employee who only ever opens My Leave should download neither.
 *  · **Route guards are UX, not security.** `<Workspaces allow={…}>` keeps someone
 *    from landing on a page their nav never offered — the server independently scopes
 *    and rejects every request behind it. Nothing here is load-bearing for access.
 *    It admits a viewer on their workspace *or* on the capability `ROUTE_CAPABILITY`
 *    records for that address, because a workspace and a permission are not the same
 *    fact — see the note on the guard itself.
 *
 * Screens owned by other agents are imported by path only. `screen()` accepts either a
 * default export or a named one so the wiring survives whichever style they choose.
 */
import { lazy, type ComponentType, type ReactNode } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AppShell } from "@/components/shell/AppShell";
import { RequireAuth } from "@/components/shell/RequireAuth";
import { SignIn } from "@/components/shell/SignIn";
import {
  LANDING_LABEL,
  LANDING_ROUTE,
  ROUTE_CAPABILITY,
  type Workspace,
} from "@/components/shell/Sidebar";
import { Btn, EmptyState, SectionTitle } from "@/components/ui";
import { useMe } from "@/lib/session";

/* ------------------------------ lazy screens ------------------------------ */

type LazyModule = Record<string, unknown>;

/** Tolerates `export default X` and `export function X` alike. */
function screen(loader: () => Promise<LazyModule>, name: string) {
  return lazy(async () => {
    const module = await loader();
    const Component = (module.default ?? module[name]) as ComponentType;
    return { default: Component };
  });
}

// Mine.
const Teammates = screen(() => import("@/screens/Teammates"), "Teammates");
const OrgChart = screen(() => import("@/screens/OrgChart"), "OrgChart");
const MyProfile = screen(() => import("@/screens/MyProfile"), "MyProfile");

// Owned by other agents — imported from these exact paths.
const Dashboard = screen(() => import("@/screens/Dashboard"), "Dashboard");
const Home = screen(() => import("@/screens/Home"), "Home");
const ManagerHome = screen(() => import("@/screens/ManagerHome"), "ManagerHome");
const MyLeave = screen(() => import("@/screens/MyLeave"), "MyLeave");
const LeaveAdmin = screen(() => import("@/screens/LeaveAdmin"), "LeaveAdmin");
const Attendance = screen(() => import("@/screens/Attendance"), "Attendance");
const MyAttendance = screen(() => import("@/screens/MyAttendance"), "MyAttendance");
const Reimbursements = screen(() => import("@/screens/Reimbursements"), "Reimbursements");
const MyReimbursements = screen(
  () => import("@/screens/MyReimbursements"),
  "MyReimbursements",
);
const Reviews = screen(() => import("@/screens/Reviews"), "Reviews");
const MyReviews = screen(() => import("@/screens/MyReviews"), "MyReviews");
const Reports = screen(() => import("@/screens/Reports"), "Reports");
const Settings = screen(() => import("@/screens/Settings"), "Settings");
const Onboarding = screen(() => import("@/screens/Onboarding"), "Onboarding");
const Resources = screen(() => import("@/screens/Resources"), "Resources");
const Alerts = screen(() => import("@/screens/Alerts"), "Alerts");

/* --------------------------------- guards -------------------------------- */

/** Sends "/" to whatever the signed-in workspace calls home. */
function LandingRedirect() {
  const me = useMe();
  return <Navigate to={LANDING_ROUTE[me.workspace]} replace />;
}

/**
 * Where an email's button lands, per workspace.
 *
 * An email cannot know which workspace its reader signs into: the approver of a
 * leave request is a manager for most people and a Super Admin for others, and the
 * two approve from different screens (`/team` is closed to the admin workspace).
 * So mail links to `/go/<target>` and this table picks the screen on arrival.
 */
const EMAIL_TARGETS: Record<string, Record<Workspace, string>> = {
  "leave-approvals": { admin: "/admin/leave", manager: "/team", employee: "/home" },
  "claim-approvals": { admin: "/admin/reimbursements", manager: "/team/reimbursements", employee: "/home" },
  reviews: { admin: "/admin/reviews", manager: "/team", employee: "/me/reviews" },
  team: { admin: "/admin/teammates", manager: "/team", employee: "/home" },
};

function EmailTargetRedirect() {
  const me = useMe();
  const { target = "" } = useParams();
  const destination = EMAIL_TARGETS[target]?.[me.workspace] ?? LANDING_ROUTE[me.workspace];
  return <Navigate to={destination} replace />;
}

/**
 * The way out, wherever "out" is for this viewer.
 *
 * Both dead-end screens need it and there is one right answer per workspace, so it
 * lives in one place rather than as an expression inlined into each. A `Link` rather
 * than an `<a href>`: this is in-app navigation and a full document reload would
 * re-boot the SPA, re-fetch `/me` and discard the query cache to travel one route.
 */
function BackToLanding() {
  const me = useMe();
  return (
    <Link to={LANDING_ROUTE[me.workspace]} style={{ textDecoration: "none" }}>
      <Btn>Back to {LANDING_LABEL[me.workspace]}</Btn>
    </Link>
  );
}

export function Forbidden() {
  const location = useLocation();
  return (
    <div>
      <SectionTitle sub={location.pathname}>Not available to you</SectionTitle>
      <EmptyState title="This page isn't part of your workspace." action={<BackToLanding />}>
        If you think you should have access, ask HR to check your role.
      </EmptyState>
    </div>
  );
}

/**
 * A mistyped address is not a cul-de-sac.
 *
 * This screen had no control on it at all: the rail is beside it, but somebody who
 * arrived from a stale bookmark or a truncated link got a sentence and nothing to
 * press, while its sibling one function up offered a way home. Same escape hatch,
 * same destination logic — whatever this viewer's workspace calls home.
 */
function NotFound() {
  const location = useLocation();
  return (
    <div>
      <SectionTitle sub={location.pathname}>Page not found</SectionTitle>
      <EmptyState title="That address doesn’t exist in BotpressoHR." action={<BackToLanding />}>
        Check the link, or pick the screen you wanted from the menu.
      </EmptyState>
    </div>
  );
}

/**
 * Renders `children` when the viewer may open this address, and the Forbidden screen
 * when they may not. Server-side authorization is unaffected either way.
 *
 * Two ways through, because there are two reasons to be allowed on a page.
 *
 * The workspace is the usual one. The second is a **capability**, and it exists
 * because the two are genuinely different questions: `workspaceFor` derives the rail
 * from the effective role, so a Finance user with no reports is a `member` in the
 * employee workspace, while `capabilitiesFor` independently grants that access role
 * `canViewReports` and `canPayClaims`. Gating on workspace alone therefore refused
 * Finance the two screens the server exists to serve them.
 *
 * The capability is looked up by pathname against `ROUTE_CAPABILITY`, the same table
 * the sidebar builds its extra rail items from. That is not a shortcut — it is what
 * makes "the rail shows only what you can open" true by construction rather than by
 * two lists someone has to keep in step.
 */
function Workspaces({ allow, children }: { allow: readonly Workspace[]; children: ReactNode }) {
  const me = useMe();
  const { pathname } = useLocation();
  if (allow.includes(me.workspace)) return <>{children}</>;

  // Trailing slashes are legal in a typed URL and would miss the table otherwise.
  const capability = ROUTE_CAPABILITY[pathname.replace(/\/+$/, "")];
  if (capability && me.capabilities[capability]) return <>{children}</>;

  return <Forbidden />;
}

const ADMIN: readonly Workspace[] = ["admin"];
const MANAGER: readonly Workspace[] = ["manager"];

/* --------------------------------- routes -------------------------------- */

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<LandingRedirect />} />

          {/* Me — available in every workspace */}
          <Route path="home" element={<Home />} />
          <Route path="go/:target" element={<EmailTargetRedirect />} />
          <Route path="onboarding" element={<Onboarding />} />
          <Route path="me/profile" element={<MyProfile />} />
          <Route path="me/leave" element={<MyLeave />} />
          <Route path="me/attendance" element={<MyAttendance />} />
          <Route path="me/reviews" element={<MyReviews />} />
          <Route path="me/reimbursements" element={<MyReimbursements />} />

          {/* Company — available in every workspace */}
          <Route path="org-chart" element={<OrgChart />} />
          <Route path="resources" element={<Resources />} />
          {/* Command Center: the bell's full list. Scoped server-side, so open to all. */}
          <Route path="alerts" element={<Alerts />} />

          {/* Manager */}
          <Route
            path="team"
            element={
              <Workspaces allow={MANAGER}>
                <ManagerHome />
              </Workspaces>
            }
          />
          <Route
            path="team/reimbursements"
            element={
              <Workspaces allow={MANAGER}>
                <Reimbursements />
              </Workspaces>
            }
          />

          {/* Organisation */}
          <Route
            path="admin/dashboard"
            element={
              <Workspaces allow={ADMIN}>
                <Dashboard />
              </Workspaces>
            }
          />
          <Route
            path="admin/teammates"
            element={
              <Workspaces allow={ADMIN}>
                <Teammates />
              </Workspaces>
            }
          />
          <Route
            path="admin/leave"
            element={
              <Workspaces allow={ADMIN}>
                <LeaveAdmin />
              </Workspaces>
            }
          />
          <Route
            path="admin/attendance"
            element={
              <Workspaces allow={ADMIN}>
                <Attendance />
              </Workspaces>
            }
          />
          <Route
            path="admin/reimbursements"
            element={
              <Workspaces allow={ADMIN}>
                <Reimbursements />
              </Workspaces>
            }
          />
          <Route
            path="admin/reviews"
            element={
              <Workspaces allow={ADMIN}>
                <Reviews />
              </Workspaces>
            }
          />
          <Route
            path="admin/reports"
            element={
              <Workspaces allow={ADMIN}>
                <Reports />
              </Workspaces>
            }
          />
          <Route
            path="admin/settings"
            element={
              <Workspaces allow={ADMIN}>
                <Settings />
              </Workspaces>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default AppRoutes;
